<template>
  <ValidationObserver v-slot="{ invalid }">
    <form class="chi-form" @submit.prevent="onSubmit">
      <div class="mb-4 flex flex-col">
        <h2>Parents Information</h2>
        <!-- single parent -->
        <div class="w-full mb-3">
          <label for="singleParent">
            Are you a single parent?
            <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            name="radioSingleParent"
            v-slot="{ classes, errors }"
          >
            <div class="mb-4 w-full pb-5">
              <div class="radio-toolbar">
                <input
                  :class="classes"
                  v-model="singleParent"
                  type="radio"
                  id="radioSingleParentYes"
                  name="singleParent"
                  :value="true"
                />
                <label for="radioSingleParentYes">Yes</label>

                <input
                  :class="classes"
                  v-model="singleParent"
                  type="radio"
                  id="radioSingleParentNo"
                  name="singleParent"
                  :value="false"
                />
                <label for="radioSingleParentNo">No</label>
              </div>
            </div>
            <p class="errors">{{ errors[0] }}</p>
          </ValidationProvider>
        </div>

        <!-- parent 1: self -->
        <h3
          v-if="!singleParent"
          class="mb-3 font-semibold text-purple-ap-100 text-sm uppercase"
        >
          first parent
        </h3>
        <div class="flex flex-col lg:flex-row">
          <!-- parent 1 first name -->
          <div class="w-full lg:w-1/3 md:mr-3 mb-3">
            <ChiInput
              type="text"
              rules="required|alpha_spaces"
              name="first name"
              label="First Name"
              :placeholder="firstParentFirstName"
              v-model="parents.first.firstName"
            />
          </div>
          <!-- parent 1 last name -->
          <div class="w-full lg:w-1/3 md:mr-3 mb-3">
            <ChiInput
              type="text"
              rules="required|alpha_spaces"
              name="last name"
              label="Last Name"
              :placeholder="firstParentLastName"
              v-model="parents.first.lastName"
            />
          </div>
          <!-- parent 1 birth date -->
          <div class="w-full lg:w-1/3 mb-3">
            <label for="grid-parent-1-birthdate">
              Birthdate
            </label>
            <ValidationProvider
              name="birthdate"
              rules="required"
              v-slot="{ classes, errors }"
            >
              <datepicker
                :required="true"
                :typeable="true"
                :format="'MMMM d, yyyy'"
                :language="en"
                :open-date="dateFormatter(-25)"
                :disabled-dates="{
                  to: dateFormatter(-60),
                  from: dateFormatter(-18),
                }"
                v-model="parents.first.birthdate"
                :input-class="classes"
                id="grid-parent-1-birthdate"
              ></datepicker>
              <p class="errors">{{ errors[0] }}</p>
            </ValidationProvider>
          </div>
        </div>

        <!-- parent 2 -->
        <div class="mt-4" v-if="!singleParent">
          <h4 class="mb-3 font-semibold text-purple-ap-100 text-sm uppercase">
            second parent
          </h4>
          <div class="flex flex-col lg:flex-row">
            <!-- parent 2 first name -->
            <div class="w-full lg:w-1/3 mr-3 mb-3">
              <ChiInput
                type="text"
                rules="required|alpha_spaces"
                name="first name"
                label="First Name"
                placeholder="Second parent first name"
                v-model="parents.second.firstName"
              />
            </div>
            <!-- parent 2 last name -->
            <div class="w-full lg:w-1/3 mr-3 mb-3">
              <ChiInput
                type="text"
                rules="required|alpha_spaces"
                name="last name"
                label="Last Name"
                placeholder="Second parent last name"
                v-model="parents.second.lastName"
              />
            </div>
            <!-- parent 2 birth date -->
            <div class="w-full lg:w-1/3 mb-3">
              <label for="grid-parent-2-birthdate">
                Birthdate
              </label>
              <ValidationProvider
                name="birthdate"
                rules="required"
                v-slot="{ classes, errors }"
              >
                <datepicker
                  :required="true"
                  :typeable="true"
                  :format="'MMMM d, yyyy'"
                  :language="en"
                  :open-date="dateFormatter(-25)"
                  :disabled-dates="{
                    to: dateFormatter(-60),
                    from: dateFormatter(-18),
                  }"
                  :input-class="classes"
                  v-model="parents.second.birthdate"
                  id="grid-parent-2-birthdate"
                ></datepicker>
                <p class="errors">{{ errors[0] }}</p>
              </ValidationProvider>
            </div>
          </div>
        </div>

        <div class="w-full mt-4 mb-3">
          <ChiInput
            type="tel"
            rules="required|digits:10"
            name="phone"
            label="Phone"
            placeholder="Your Phone"
            v-model="phone"
            maxlength="10"
          />
        </div>

        <h2 class="mt-8">Languages Information</h2>
        <!-- primarily spoken languge -->
        <div class="w-full mt-4 mb-3">
          <ChiInput
            type="text"
            rules="required"
            name="primary language"
            label="What primary language is spoken at home?"
            placeholder="English"
            v-model="primaryLanguage"
          />
        </div>

        <!-- other spoken languges -->
        <div class="w-full my-4 mb-3">
          <ChiInput
            type="text"
            optional="true"
            name="other languages"
            label="What other language(s) do family members speak?"
            placeholder="English"
            v-model="otherLanguages"
          />
        </div>

        <h2 class="mt-6">About Your Au Pair</h2>
        <!-- au pair arrival -->
        <div class="w-full mt-4 mb-3">
          <label
            class="flex items-center justify-between"
            for="grid-aupair-arrival"
          >
            <span>When would you like the Au Pair to arrive?</span>
          </label>
          <ValidationProvider
            name="au pair arrival date"
            rules="required"
            v-slot="{ classes, errors }"
          >
            <datepicker
              :typeable="true"
              :format="'MMMM d, yyyy'"
              :language="en"
              :disabled-dates="{
                to: new Date(),
              }"
              :input-class="classes"
              v-model="auPairArrival"
              id="grid-aupair-arrival"
            ></datepicker>
            <p class="errors">{{ errors[0] }}</p>
          </ValidationProvider>
        </div>

        <!-- view hf profile -->
        <div class="w-full mb-3">
          <label for="grid-first-name">
            If your interest request is approved, would you allow the Au Pair to
            view your Host Family profile?
          </label>
          <ValidationProvider
            name="viewHFProfile"
            rules="required"
            v-slot="{ classes, errors }"
          >
            <div class="mb-4 w-full">
              <div class="radio-toolbar">
                <input
                  :class="classes"
                  v-model="viewHFProfile"
                  type="radio"
                  id="viewHFProfileYes"
                  name="viewHFProfile"
                  :value="true"
                />
                <label for="viewHFProfileYes">Yes</label>

                <input
                  :class="classes"
                  v-model="viewHFProfile"
                  type="radio"
                  id="viewHFProfileNo"
                  name="viewHFProfile"
                  :value="false"
                />
                <label for="viewHFProfileNo">No</label>
              </div>
            </div>
            <p class="errors">{{ errors[0] }}</p>
          </ValidationProvider>
        </div>

        <!-- special needs handling -->
        <div class="w-full mb-3">
          <label for="grid-first-name">
            Does your child require an Au Pair with a specialized background
            with handling special needs challenges and/or any medical
            conditions?
          </label>
          <ValidationProvider
            name="specialNeeds"
            rules="required"
            v-slot="{ classes, errors }"
          >
            <div class="mb-4 w-full pb-5">
              <div class="radio-toolbar">
                <input
                  :class="classes"
                  v-model="specialNeeds"
                  type="radio"
                  id="specialNeedsYes"
                  name="specialNeeds"
                  :value="true"
                />
                <label for="specialNeedsYes">Yes</label>

                <input
                  :class="classes"
                  v-model="specialNeeds"
                  type="radio"
                  id="specialNeedsNo"
                  name="specialNeeds"
                  :value="false"
                />
                <label for="specialNeedsNo">No</label>
              </div>
            </div>
            <p class="errors">{{ errors[0] }}</p>
          </ValidationProvider>
        </div>

        <!-- special needs -->
        <div v-if="specialNeeds" class="w-full mb-3">
          <ChiInput
            type="textarea"
            rules="required"
            name="special needs"
            label="Please describe the specialized background required"
            placeholder="Enter your notes here"
            v-model="specialNeedsNotes"
          />
        </div>

        <h2 class="mt-6">About Your Family</h2>

        <!-- Age and Gender -->
        <div class="w-full mt-4 mb-3">
          <ChiInput
            type="textarea"
            optional="true"
            name="childrenAgeAndGender"
            label="Please share the age and gender of your child(en)"
            placeholder="Type your message"
            v-model="childrenAgeAndGender"
          />
        </div>

        <!-- previously had and au pair or nany -->
        <div class="w-full mb-3">
          <label for="previouslyHadAuPairOrNanny">
            Have you previously had an au pair/nanny?
          </label>

          <div class="mb-4 w-full">
            <div class="radio-toolbar">
              <input
                v-model="previouslyHadAuPairOrNanny"
                type="radio"
                id="previouslyHadAuPairOrNannyYes"
                name="previouslyHadAuPairOrNanny"
                :value="true"
              />
              <label for="previouslyHadAuPairOrNannyYes">Yes</label>

              <input
                v-model="previouslyHadAuPairOrNanny"
                type="radio"
                id="previouslyHadAuPairOrNannyNo"
                name="previouslyHadAuPairOrNanny"
                :value="false"
              />
              <label for="previouslyHadAuPairOrNannyNo">No</label>
            </div>
          </div>
        </div>

        <!-- ever lived abroad -->
        <div class="w-full mb-3">
          <label for="everLivedAbroad">
            Have you ever lived abroad?
          </label>

          <div class="mb-4 w-full">
            <div class="radio-toolbar">
              <input
                v-model="everLivedAbroad"
                type="radio"
                id="everLivedAbroadYes"
                name="everLivedAbroad"
                :value="true"
              />
              <label for="everLivedAbroadYes">Yes</label>

              <input
                v-model="everLivedAbroad"
                type="radio"
                id="everLivedAbroadNo"
                name="everLivedAbroad"
                :value="false"
              />
              <label for="everLivedAbroadNo">No</label>
            </div>
          </div>
        </div>

        <!-- ever lived abroad -->
        <div class="w-full mb-3">
          <label for="travelForWork">
            Do you travel for work?
          </label>

          <div class="mb-4 w-full">
            <div class="radio-toolbar">
              <input
                v-model="travelForWork"
                type="radio"
                id="travelForWorkYes"
                name="travelForWork"
                :value="true"
              />
              <label for="travelForWorkYes">Yes</label>

              <input
                v-model="travelForWork"
                type="radio"
                id="travelForWorkNo"
                name="travelForWork"
                :value="false"
              />
              <label for="travelForWorkNo">No</label>
            </div>
          </div>
        </div>

        <!-- message -->
        <div class="w-full mt-4 mb-3">
          <ChiInput
            type="textarea"
            optional="true"
            name="message"
            label="Anything else you are looking for?"
            placeholder="Type your message"
            v-model="message"
          />
        </div>
      </div>

      <div class="-mx-3 md:flex mb-2">
        <div class="w-full flex flex-col items-center justify-center mt-5">
          <input
            :disabled="invalid"
            type="submit"
            class="btn btn-purple apply"
            value="Submit my Application"
          />
        </div>
      </div>
    </form>
    <PageLoading v-if="loading" />
  </ValidationObserver>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import ChiInput from "@/components/Forms/ChiInput.vue";
import { en } from "vuejs-datepicker/dist/locale";
import { api } from "@/services.js";

export default {
  name: "ApplicationForm",
  components: {
    Datepicker,
    ChiInput,
  },
  data() {
    return {
      en: en,
      loading: false,
      singleParent: false,
      phone: this.$store.state.user.phone,
      parents: {
        first: {
          firstName: this.$store.state.user.firstName,
          lastName: this.$store.state.user.lastName,
          birthdate: null,
        },
        second: {
          firstName: null,
          lastName: null,
          birthdate: null,
        },
      },
      primaryLanguage: "English",
      otherLanguages: null,
      auPairArrival: null,
      viewHFProfile: null,
      specialNeeds: null,
      specialNeedsNotes: null,
      childrenAgeAndGender: null,
      previouslyHadAuPairOrNanny: null,
      everLivedAbroad: null,
      travelForWork: null,
      message: null,
    };
  },
  methods: {
    onSubmit() {
      this.loading = true;

      let p1bd = this.parents.first.birthdate
        ? this.parents.first.birthdate.toISOString()
        : null;
      let p2bd = this.parents.second.birthdate
        ? this.parents.second.birthdate.toISOString()
        : null;
      let ad = this.auPairArrival ? this.auPairArrival.toISOString() : null;

      const submission = {
        hostFamily: {
          firstName: this.parents.first.firstName,
          lastName: this.parents.first.lastName,
          birthDate: p1bd,
          primaryPhone: this.phone,
          singleParent: this.singleParent,
          secondParentFirstName: this.parents.second.firstName,
          secondParentLastName: this.parents.second.lastName,
          secondParentBirthDate: p2bd,
          citizenOrResident: this.$store.state.user.citizenOrResident,
          hasChildrenOrPlanTo: this.$store.state.user.hasChildrenOrPlanTo,
          hostFamilyUserId: this.$store.state.user.id,
        },
        matchRequest: {
          auPairPromoProfileId: this.$store.state.user.matchRequest,
          primaryLanguageSpoken: this.primaryLanguage,
          otherLanguagesSpoken: this.otherLanguages,
          idealArrivalDate: ad,
          allowAuPairToViewHostFamilyProfile: this.viewHFProfile,
          specialNeedsHandling: this.specialNeeds,
          specialNeedsComments: this.specialNeedsNotes,
          childrenAgeAndGender: this.childrenAgeAndGender,
          previouslyHadAuPairOrNanny: this.previouslyHadAuPairOrNanny,
          everLivedAbroad: this.everLivedAbroad,
          travelForWork: this.travelForWork,
          messageToCHI: this.message,
        },
      };

      api
        .post("/promotional/AuPairPromoProfiles/application", submission)
        .then(() => {
          this.$toasted.global.notificate({
            message: "Your application was sent. Thank you!",
            type: "success",
          });
          this.$store.commit("UPDATE_USER", {
            match: this.$store.state.user.matchRequest,
          });
          this.$router.push("/");
          this.loading = false;
        })
        .catch((error) => {
          this.$toasted.global.notificate({
            message: error.response.data.message,
            type: "error",
          });
          this.loading = false;
        });
    },
    dateFormatter(yearDiff) {
      let current = new Date();
      let year = current.getFullYear() + yearDiff;
      return new Date(year, current.getMonth(), current.getDay());
    },
  },
  created() {},
  computed: {
    firstParentFirstName() {
      return this.$store.state.user.firstName;
    },
    firstParentLastName() {
      return this.$store.state.user.lastName;
    },
    isSingleParent() {
      if (this.singleParent === "yes") return true;
      return false;
    },
  },
};
</script>

<style scoped>
.radio-toolbar input[type="radio"] {
  opacity: 0;
  position: fixed;
  width: 0;
}

.radio-toolbar label {
  display: inline-block;
  color: #555;
  background-color: #fff;
  font-size: 0.8rem;
  padding: 8px 20px;
  border-radius: 2px;
  border: 1px solid rgba(100, 100, 100, 0.1);
  cursor: pointer;
  transition: all 0.3s;
}

.radio-toolbar input[type="radio"]:checked + label {
  background-color: #9a69cb;
  border-color: transparent;
  color: #fff;
}

.radio-toolbar input[type="radio"]:focus + label {
  border-color: transparent;
}

.radio-toolbar label:hover {
  background-color: rgba(154, 105, 203, 0.6);
  border-color: transparent;
  color: #fff;
  transition: all 0.3s;
}

.text-muted {
  font-size: 0.8rem;
  font-style: italic;
  font-weight: 400;
}
</style>
